import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MapPin, Bed, Bath, Square, Car } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useCurrency } from '../contexts/CurrencyContext';
import GenericHero from '../components/GenericHero';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';

const PropertyPage = () => {
  const { properties, propertyCategories, loading, error } = useData();
  const { formatPrice } = useCurrency();
  const [listingFilter, setListingFilter] = useState('sale');
  const [locationFilter, setLocationFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState({ min: '', max: '' });
  const autoplayPlugin = React.useMemo(() => {
    return [
      Autoplay({
        delay: 5000,
        stopOnInteraction: true,
      }),
    ];
  }, []);

  const renderPrice = (price, fallback = 'Contact for Price') => {
    if (!price) return fallback;
    return formatPrice(price);
  };

  const deriveListingType = (property) => {
    const raw =
      (property.listing_type ||
        property.transaction_type ||
        property.offer_type ||
        property.status ||
        property.category ||
        '')
        .toString()
        .toLowerCase();

    if (raw.includes('rent') || raw.includes('lease')) return 'rent';
    if (raw.includes('sale') || raw.includes('sell') || raw.includes('buy')) return 'sale';
    return 'sale';
  };

  const locationOptions = useMemo(() => {
    const set = new Set();
    (properties || []).forEach((property) => {
      if (property.location) {
        set.add(property.location.trim());
      }
    });
    return Array.from(set);
  }, [properties]);

  const getCategoryName = (categoryValue) => {
    if (!categoryValue) return null;
    const directMatch = (propertyCategories || []).find(
      (cat) => cat.id === categoryValue || cat.name?.toLowerCase() === categoryValue.toLowerCase()
    );
    if (directMatch) return directMatch.name;
    const fallback = (propertyCategories || []).find(
      (cat) => cat.name?.toLowerCase() === categoryValue.toString().toLowerCase()
    );
    return fallback ? fallback.name : categoryValue;
  };

  const categoryOptions = useMemo(() => {
    if (!Array.isArray(propertyCategories)) return [];
    return propertyCategories.map((category) => ({
      id: category.id,
      name: category.name,
    }));
  }, [propertyCategories]);

  const filteredProperties = useMemo(() => {
    return (properties || []).filter((property) => {
      const listingType = deriveListingType(property);
      if (listingType !== listingFilter) return false;

      if (
        locationFilter !== 'all' &&
        property.location?.toLowerCase() !== locationFilter.toLowerCase()
      ) {
        return false;
      }

      if (categoryFilter !== 'all') {
        const categoryValue = (property.category || '').toString();
        const normalizedFilter = categoryFilter.toLowerCase();
        const categoryName = getCategoryName(categoryValue)?.toLowerCase();
        if (
          categoryValue.toLowerCase() !== normalizedFilter &&
          categoryName !== normalizedFilter
        ) {
          return false;
        }
      }

      const priceValue = Number(property.price) || 0;
      if (priceFilter.min !== '' && priceValue < Number(priceFilter.min)) return false;
      if (priceFilter.max !== '' && priceValue > Number(priceFilter.max)) return false;

      return true;
    });
  }, [properties, listingFilter, locationFilter, priceFilter, categoryFilter, propertyCategories]);

  const featuredProperties = useMemo(
    () => filteredProperties.filter((property) => property.is_featured).slice(0, 4),
    [filteredProperties]
  );

  if (loading) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-800 mx-auto mb-4"></div>
        <p className="text-stone-600">Loading properties...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="pt-20 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-red-600 mb-4">Error loading properties</p>
        <p className="text-stone-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="pt-20">
      {/* Hero Section with video background */}
      <div className="absolute inset-0 h-[40vh] w-full -z-10">
        <img
          src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
          alt="Properties"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>
      <div className="pt-0">
        <GenericHero
          title="Properties"
          description="Discover exceptional properties crafted for modern living. Each residence represents the pinnacle of luxury and design excellence."
          backgroundVideo="https://ik.imagekit.io/hqhiltiie/Nyinimu/hero%20bg.mp4?updatedAt=1763033048497"
        />
      </div>

      {/* Featured Properties */}
      <section className="pt-16 pb-16 bg-[#81be00]">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold title-font text-white mb-6">
              {listingFilter === 'sale' ? 'Featured Properties for Sale' : 'Featured Rentals'}
            </h2>
            <p className="text-white text-lg">
              Handpicked selection of our most exclusive properties
            </p>
            <div className="mt-8 pb-8 border-b border-white"></div>
          </div>

          {featuredProperties.length === 0 ? (
            <div className="text-center text-stone-500">
              No featured listings match your filters right now.
            </div>
          ) : (
            <div className="relative w-full px-16">
              <Carousel 
                opts={{ align: "start", loop: true }} 
                plugins={autoplayPlugin}
                className="w-full"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {featuredProperties.map((property) => (
                    <CarouselItem key={property.id} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                      <Card className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden h-full">
                        <div className="relative">
                          <img
                            src={property.featured_image || property.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80'}
                            alt={property.title}
                            className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div className="absolute top-4 left-4">
                            <div className="bg-gradient-to-r from-[#81be00] to-[#6a9e00] text-white px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full shadow-lg backdrop-blur-sm border border-white/20 flex items-center gap-1.5">
                              <span className="text-sm">✨</span>
                              <span>Featured</span>
                            </div>
                          </div>
                          <div className="absolute top-4 right-4">
                            <span className="bg-white bg-opacity-90 text-stone-800 px-3 py-1 text-sm font-semibold">
                              {renderPrice(property.price)}
                            </span>
                          </div>
                        </div>

                        <CardContent className="p-4">
                          <p className="text-xs font-semibold tracking-[0.3em] text-[#81be00] uppercase mb-2">
                            {getCategoryName(property.category) || 'Property'}
                          </p>
                          <h3 className="text-xl font-medium title-font text-stone-800 mb-2">
                            {property.title}
                          </h3>

                          <div className="flex items-center text-[#81be00] mb-2">
                            <MapPin size={16} className="mr-2" />
                            <span className="text-sm">{property.location || 'Location Available on Request'}</span>
                          </div>

                          <div className="mb-3">
                            <p className="text-stone-600 text-sm leading-relaxed">
                              {property.short_description || 'Property description available upon inquiry.'}
                            </p>
                          </div>

                          <div className="flex items-center justify-between mb-4">
                            <div className="text-lg font-semibold text-stone-800">
                              {renderPrice(property.price, 'Price on Request')}
                            </div>
                            {property.is_featured && (
                              <div className="flex items-center text-[#81be00] text-sm font-semibold bg-white/90 px-2 py-1 rounded-full shadow-md border border-[#81be00]/20">
                                <span className="mr-1 text-yellow-500">✨</span>
                                Featured
                              </div>
                            )}
                          </div>

                          {property.gallery && property.gallery.length > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center text-green-600 text-xs font-medium">
                                📷 {property.gallery.length} Photos Available
                              </div>
                            </div>
                          )}

                          {property.floor_plan && (
                            <div className="mb-3">
                              <div className="flex items-center text-green-600 text-xs font-medium">
                                📋 Floor Plan Available
                              </div>
                            </div>
                          )}

                          <Link to={`/property/${property.id}`}>
                            <Button size="sm" className="w-full bg-[#7b513a] hover:bg-[#81be00] text-white transition-all duration-300 hover:shadow-lg hover:scale-105">
                              View Details
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="absolute left-0 top-1/2 -translate-y-1/2 h-10 w-10 bg-white hover:bg-stone-100 text-stone-800 shadow-lg border-0 z-10" />
                <CarouselNext className="absolute right-0 top-1/2 -translate-y-1/2 h-10 w-10 bg-white hover:bg-stone-100 text-stone-800 shadow-lg border-0 z-10" />
              </Carousel>
            </div>
          )}
        </div>
      </section>

      {/* All Properties */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-semibold title-font text-[#81be00] mb-6">
              All Properties
            </h2>
            <p className="text-stone-600 text-lg">
              Browse our complete collection of premium properties
            </p>
          </div>

          {filteredProperties.length === 0 ? (
            <div className="text-center py-12 text-stone-500">
              No properties match your filters. Try adjusting the options above.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProperties.map((property) => (
              <Card key={property.id} className="group cursor-pointer border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                <div className="relative">
                  <img
                    src={property.featured_image || property.images?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80'}
                    alt={property.title}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="bg-white bg-opacity-90 text-stone-800 px-3 py-1 text-sm font-semibold">
                      {renderPrice(property.price)}
                    </span>
                  </div>
                  {property.is_featured && (
                    <div className="absolute top-3 left-3">
                      <div className="bg-gradient-to-r from-[#81be00] to-[#6a9e00] text-white px-2.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full shadow-lg backdrop-blur-sm border border-white/20 flex items-center gap-1">
                        <span className="text-xs">✨</span>
                        <span>Featured</span>
                      </div>
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <p className="text-[11px] font-semibold tracking-[0.3em] text-[#81be00] uppercase mb-1">
                    {getCategoryName(property.category) || 'Property'}
                  </p>
                  <h3 className="text-lg font-medium title-font text-stone-800 mb-2">
                    {property.title}
                  </h3>

                  <div className="flex items-center text-[#81be00] mb-3">
                    <MapPin size={14} className="mr-1" />
                    <span className="text-xs">{property.location || 'Location Available on Request'}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {property.category === 'land' ? (
                      <>
                        <div className="flex items-center text-stone-600">
                          <Square size={14} className="mr-1" />
                          <span className="text-xs">{property.land_size || 0} {property.land_size_unit || 'acres'}</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <MapPin size={14} className="mr-1" />
                          <span className="text-xs">Land Plot</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <span className="text-xs">Available</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <span className="text-xs">For Sale</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center text-stone-600">
                          <Bed size={14} className="mr-1" />
                          <span className="text-xs">{property.bedrooms || 0}</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <Bath size={14} className="mr-1" />
                          <span className="text-xs">{property.bathrooms || 0}</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <Square size={14} className="mr-1" />
                          <span className="text-xs">{property.square_feet || 0} sq ft</span>
                        </div>
                        <div className="flex items-center text-stone-600">
                          <Car size={14} className="mr-1" />
                          <span className="text-xs">{property.parking_spaces || 0}</span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Short Description */}
                  {property.short_description && (
                    <div className="mb-3">
                      <p className="text-stone-600 text-xs leading-relaxed line-clamp-2">
                        {property.short_description}
                      </p>
                    </div>
                  )}

                  <Link to={`/property/${property.id}`}>
                    <Button size="sm" className="w-full bg-[#7b513a] hover:bg-[#81be00] text-white transition-all duration-300 hover:shadow-lg hover:scale-105">
                      View Details
                    </Button>
                  </Link>
                </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-stone-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-light title-font text-white mb-6">
            Can't Find What You're Looking For?
          </h2>
          <p className="text-stone-300 text-lg mb-8">
            Our team of experts is ready to help you find the perfect property that matches your needs and lifestyle.
          </p>
          <Button className="bg-[#81be00] hover:bg-[#81be00] text-stone-900 px-8 py-3 text-lg font-medium transition-colors duration-300">
            Contact Our Team
          </Button>
        </div>
      </section>
    </div>
  );
};

export default PropertyPage;
